import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, RotateCcw, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { useReportDraft } from './useReportDraft';

const FLOOR_HELP =
  'Pre-filled from portal data — a minimum. Enter your office\'s full monthly totals including walk-ins and off-platform activity.';

/**
 * SPRS editor: the fixed two-page government outline. Section headers render bold and
 * un-valued; value leaves take an accomplishment and (where the form has one) a female
 * figure. Platform-derived leaves prefill as an editable floor, flagged so staff know to
 * revise them upward. No add/delete — the outline is fixed. Persistence is shared with
 * the table editor via useReportDraft; SPRS edits are node-id overrides of {accomplishment,
 * female}.
 */
export default function SprsEditor({ reportType, period, authHeaders, onBack, settings, officeName }) {
  const { t } = useTranslation();
  const draft = useReportDraft(reportType, period, authHeaders);
  const { data, deltas, meta, loading, loadError, saving, busy, busyRef, load, commit, applyAndReload, reset, download } = draft;

  const [nodes, setNodes] = useState([]);
  useEffect(() => { setNodes(data?.nodes || []); }, [data]);

  const editNode = (id, field, value) => {
    if (busyRef.current || !deltas) return;
    setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, [field]: value } : n)));
    const next = { ...deltas, overrides: { ...deltas.overrides, [id]: { ...(deltas.overrides[id] || {}), [field]: value } } };
    commit(next);
  };

  // Per-field, like the row editor: reverting one cell must not destroy an edit the
  // staffer made to the sibling cell on the same node.
  const revertNode = (id, field) => applyAndReload((current) => {
    const { [field]: _removed, ...rest } = current.overrides[id] || {};
    const nextOverrides = { ...current.overrides };
    if (Object.keys(rest).length) nextOverrides[id] = rest; else delete nextOverrides[id];
    return { ...current, overrides: nextOverrides };
  });

  const resetAll = () => {
    if (busyRef.current) return;
    if (!window.confirm(t('peso.reportResetConfirm', 'Discard all edits and reload platform data?'))) return;
    reset();
  };

  const isOverridden = (id, field) => deltas?.overrides?.[id]?.[field] !== undefined;

  const valueCell = (n, field) => {
    const over = isOverridden(n.id, field);
    const platformFloor = n.source === 'platform';
    // Most leaves are counts; a few (e.g. "PESO institutionalized" = YES) are text, so a
    // number input would physically block the real value.
    const isText = n.value_type === 'text';
    const onChange = (e) => editNode(n.id, field,
      isText ? e.target.value : (e.target.value === '' ? '' : Number(e.target.value)));
    return (
      <div className={`relative ${over ? 'ring-1 ring-amber-400 bg-amber-50 rounded' : platformFloor ? 'bg-blue-50/40 rounded' : ''}`}>
        <Input value={n[field] ?? ''} type={isText ? 'text' : 'number'} onChange={onChange}
          className="h-8 text-sm border-transparent bg-transparent" />
        {over && (
          <button type="button" title={t('peso.revertCell', 'Revert to platform data')}
            onClick={() => revertNode(n.id, field)} className="absolute -top-1 -right-1 text-amber-600 hover:text-amber-800">
            <X size={12} />
          </button>
        )}
      </div>
    );
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">{t('peso.loading', 'Loading…')}</div>;
  if (loadError || !deltas) {
    return (
      <Card>
        <CardContent className="p-6 text-sm space-y-3">
          <p>{t('peso.reportLoadFailed', 'Failed to load report')}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load}>{t('peso.retry', 'Retry')}</Button>
            <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft size={14} className="mr-1" /> {t('peso.back', 'Back')}</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const monthLabel = new Date(`${period}-01T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const sigs = settings?.signatories || {};
  const anyFloor = nodes.some((n) => n.source === 'platform');

  const headerRow = (
    <tr className="bg-slate-100">
      <th className="border px-2 py-1 text-left text-xs font-bold min-w-[280px]">PROGRAM/PROJECT/ACTIVITIES</th>
      <th className="border px-2 py-1 text-left text-xs font-bold w-[140px]">Accomplishment</th>
      <th className="border px-2 py-1 text-left text-xs font-bold w-[110px]">FEMALE</th>
    </tr>
  );

  const renderNode = (n) => {
    if (n.kind === 'header') {
      return (
        <tr key={n.id} className="bg-slate-50">
          <td className="border px-2 py-1 font-bold text-slate-800" colSpan={3}
            style={{ paddingLeft: `${8 + n.indent * 16}px` }}>{n.label}</td>
        </tr>
      );
    }
    return (
      <tr key={n.id}>
        <td className="border px-2 py-0.5 text-slate-700" style={{ paddingLeft: `${8 + n.indent * 16}px` }}>
          {n.label}
          {n.source === 'platform' && (
            <span title={FLOOR_HELP} className="ml-1.5 inline-block text-[9px] font-semibold text-blue-700 bg-blue-100 rounded px-1 align-middle">
              min
            </span>
          )}
        </td>
        <td className="border px-1 py-0.5">{valueCell(n, 'accomplishment')}</td>
        <td className="border px-1 py-0.5">{n.female_applicable ? valueCell(n, 'female') : <span className="text-slate-300 text-xs px-2">—</span>}</td>
      </tr>
    );
  };

  const page1 = nodes.filter((n) => n.page === 1);
  const page2 = nodes.filter((n) => n.page === 2);

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
            <ArrowLeft size={14} className="mr-1" /> {t('peso.backToReports', 'All reports')}
          </Button>
          <CardTitle className="text-sm mt-1">SPRS Monthly Report — {monthLabel}</CardTitle>
          {meta?.updated_by_name && (
            <p className="text-xs text-muted-foreground">{t('peso.lastEditedBy', 'Last edited by')} {meta.updated_by_name}</p>
          )}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <span className="text-xs text-muted-foreground min-w-[52px]">
            {saving ? t('peso.saving', 'Saving…') : meta?.draft_exists ? t('peso.saved', 'Saved') : ''}
          </span>
          <Button variant="outline" size="sm" onClick={resetAll} disabled={busy}>
            <RotateCcw size={14} className="mr-1" /> {t('peso.reportReset', 'Reset')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => download('docx')}><Download size={14} className="mr-1" /> Word</Button>
          <Button variant="outline" size="sm" onClick={() => download('xlsx')}><Download size={14} className="mr-1" /> Excel</Button>
        </div>
      </CardHeader>

      <CardContent>
        <div className="text-center text-xs leading-5 mb-3">
          <div className="font-bold">STATISTICAL AND PERFORMANCE REPORTING SYSTEM (SPRS)</div>
          <div>(PESO MONTHLY REPORT)</div>
          <div>LGU {(officeName || '').toUpperCase()}</div>
          <div>REFERENCE MONTH: {monthLabel}</div>
        </div>

        {anyFloor && (
          <p className="text-[11px] text-blue-700 bg-blue-50 border border-blue-100 rounded px-3 py-2 mb-3">
            {t('peso.sprsFloorNote', 'Cells marked')} <span className="font-semibold">min</span>{' '}
            {t('peso.sprsFloorNote2', 'are pre-filled from your portal data as a minimum. Enter your office\'s full monthly totals, including walk-ins and off-platform activity.')}
          </p>
        )}

        <div className={`overflow-x-auto ${busy ? 'opacity-60 pointer-events-none' : ''}`}>
          <table className="w-full text-sm border-collapse">
            <thead>{headerRow}</thead>
            <tbody>{page1.map(renderNode)}</tbody>
          </table>
          <div className="text-[10px] text-slate-400 text-center my-2">— page 2 —</div>
          <table className="w-full text-sm border-collapse">
            <thead>{headerRow}</thead>
            <tbody>{page2.map(renderNode)}</tbody>
          </table>
        </div>

        <div className="mt-5 text-xs flex gap-12 flex-wrap">
          <div>Prepared by:<br /><span className="font-bold">{sigs.prepared_by?.name}</span><br />{sigs.prepared_by?.title}</div>
          <div>Noted by:<br /><span className="font-bold">{sigs.noted_by?.name}</span><br />{sigs.noted_by?.title}</div>
        </div>
      </CardContent>
    </Card>
  );
}
